import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RegistrationLayout } from "@/components/auth/RegistrationLayout";
import { RegistrationForm } from "@/components/auth/RegistrationForm";
import { Progress } from "@/components/ui/progress";
import { Loader2 } from "lucide-react";

const CaregiverRegistration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // Get invitation details from URL
  const invitationId = searchParams.get('invitation_id');
  const email = searchParams.get('email');

  useEffect(() => {
    if (email) {
      setFormData(prev => ({ ...prev, email }));
    }
  }, [email]);

  // Handle countdown and redirect
  useEffect(() => {
    if (redirectCountdown === null) return;
    
    if (redirectCountdown <= 0) {
      navigate("/whatsapp-community");
      return;
    }
    
    const timer = setTimeout(() => {
      setRedirectCountdown(prev => prev !== null ? prev - 1 : null);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast({
        variant: "destructive",
        title: "All fields are required",
        description: "Please fill in all the fields to continue.",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please ensure both passwords match.",
      });
      return false;
    }

    if (formData.password.length < 8) {
      toast({
        variant: "destructive",
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            role: "caregiver",
            name: formData.name,
          },
        },
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        const { data: caregiver, error: caregiverError } = await supabase
          .from('caregivers')
          .insert({
            auth_user_id: authData.user.id,
            name: formData.name,
            active: true
          })
          .select()
          .single();

        if (caregiverError) throw caregiverError;

        if (invitationId) {
          const { error: linkError } = await supabase.rpc(
            'complete_caregiver_signup',
            {
              p_invitation_id: invitationId,
              p_caregiver_id: caregiver.id
            }
          );

          if (linkError) throw linkError;
        }

        toast({
          title: "Registration successful!",
          description: "Please check your email to verify your account.",
        });
        
        // Start countdown for 10 seconds before redirecting
        setRedirectCountdown(10);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: error.message || "An unexpected error occurred",
      });
      setLoading(false);
    }
  };

  const formFields = [
    {
      id: "name",
      label: "Full Name",
      type: "text",
      placeholder: "Enter your full name",
      value: formData.name,
      onChange: handleChange,
    },
    {
      id: "email",
      label: "Email",
      type: "email",
      placeholder: "Enter your email",
      value: formData.email,
      onChange: handleChange,
      readOnly: !!email,
    },
    {
      id: "password",
      label: "Password",
      type: "password",
      placeholder: "••••••••",
      value: formData.password,
      onChange: handleChange,
      helperText: "Must be at least 8 characters",
    },
    {
      id: "confirmPassword",
      label: "Confirm Password",
      type: "password",
      placeholder: "••••••••",
      value: formData.confirmPassword,
      onChange: handleChange,
    },
  ];

  if (redirectCountdown !== null) {
    return (
      <RegistrationLayout 
        title="Account Created Successfully!"
        subtitle="Your account has been created. You'll be redirected to our WhatsApp community shortly."
      >
        <div className="flex flex-col items-center justify-center space-y-6 p-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
          
          <div className="text-center space-y-3">
            <h3 className="text-lg font-medium">Welcome to SleepNest!</h3>
            <p className="text-muted-foreground">
              Redirecting to our WhatsApp community in {redirectCountdown} seconds...
            </p>
          </div>
          
          <Progress 
            value={(redirectCountdown / 10) * 100} 
            className="w-full max-w-md h-2"
          />
          
          <button
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
            onClick={() => navigate("/whatsapp-community")}
          >
            Join now
          </button>
        </div>
      </RegistrationLayout>
    );
  }

  return (
    <RegistrationLayout 
      title="Create Your Caregiver Account"
      subtitle="Join SleepNesst and connect with your sleep consultant"
    >
      <RegistrationForm
        fields={formFields}
        onSubmit={handleSubmit}
        loading={loading}
        submitText="Create Account"
        loadingText="Creating Account..."
      />
      
      <div className="mt-6 text-center">
        <a 
          href="/whatsapp-community" 
          className="text-blue-primary hover:text-blue-600 font-medium inline-flex items-center gap-2"
        >
          <span>Learn more about our WhatsApp community</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </RegistrationLayout>
  );
};

export default CaregiverRegistration;
